import React, { useState, useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import TitleBanner from './TitleBanner';
import TestimonialsSlider from './TestimonialsSlider';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Application Modal Component
const ApplicationModal = ({ isOpen, onClose, jobTitle, jobLocation, jobCode }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contactNumber: '',
    gender: '',
    suburb: '',
    hasDriverLicense: '',
    hasQualifications: '',
    hasPoliceCheck: '',
    startDate: '',
    aboutYourself: ''
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [coverLetterFile, setCoverLetterFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Append job details
      formDataToSend.append('jobTitle', jobTitle);
      formDataToSend.append('jobLocation', jobLocation);
      formDataToSend.append('jobCode', jobCode);

      // Append files
      if (resumeFile) {
        formDataToSend.append('resume', resumeFile);
      }
      if (coverLetterFile) {
        formDataToSend.append('coverLetter', coverLetterFile);
      }

      const response = await fetch(BASE_URL+'/api/submit-job-application', {
        method: 'POST',
        body: formDataToSend,
      });

      if (response.ok) {
        alert('Application submitted successfully!');
        onClose();
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to submit application. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error submitting application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Replace the resume and cover letter link inputs with file uploads
  const fileInputs = (
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium mb-1">Resume (PDF/DOC/DOCX)</label>
        <input
          type="file"
          required
          accept=".pdf,.doc,.docx"
          className="w-full p-2 border border-purple-300 rounded"
          onChange={(e) => setResumeFile(e.target.files[0])}
        />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Cover Letter (PDF/DOC/DOCX)</label>
        <input
          type="file"
          accept=".pdf,.doc,.docx"
          className="w-full p-2 border border-purple-300 rounded"
          onChange={(e) => setCoverLetterFile(e.target.files[0])}
        />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center border-b border-gray-200 pb-4">
          <h2 className="text-2xl font-bold text-primary">Apply</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">×</button>
        </div>

        <div className="my-4">
          <h3 className="text-lg font-semibold text-primary">{jobTitle}</h3>
          <p className="text-gray-600">LOCATION: {jobLocation}</p>
          <p className="text-gray-600">CODE: {jobCode}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">First Name</label>
              <input
                type="text"
                required
                className="w-full p-2 border border-purple-300 rounded"
                onChange={(e) => setFormData({...formData, firstName: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Last Name</label>
              <input
                type="text"
                required
                className="w-full p-2 border border-purple-300 rounded"
                onChange={(e) => setFormData({...formData, lastName: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                required
                className="w-full p-2 border border-purple-300 rounded"
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contact Number</label>
              <input
                type="tel"
                required
                className="w-full p-2 border border-purple-300 rounded"
                onChange={(e) => setFormData({...formData, contactNumber: e.target.value})}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Gender</label>
            <div className="space-x-4">
              {['Female', 'Male', 'Other'].map(option => (
                <label key={option} className="inline-flex items-center">
                  <input
                    type="radio"
                    name="gender"
                    value={option}
                    className="text-primary"
                    onChange={(e) => setFormData({...formData, gender: e.target.value})}
                  />
                  <span className="ml-2">{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Your Suburb</label>
            <input
              type="text"
              required
              className="w-full p-2 border border-purple-300 rounded"
              onChange={(e) => setFormData({...formData, suburb: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Do you have an unrestricted driver's licence and access to a car for work?
            </label>
            <div className="space-x-4">
              {['Yes', 'No'].map(option => (
                <label key={option} className="inline-flex items-center">
                  <input
                    type="radio"
                    name="license"
                    value={option}
                    className="text-purple-600"
                    onChange={(e) => setFormData({...formData, hasDriverLicense: e.target.value})}
                  />
                  <span className="ml-2">{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Do you have any qualifications in Disability Support?
            </label>
            <div className="space-x-4">
              {['Yes', 'No'].map(option => (
                <label key={option} className="inline-flex items-center">
                  <input
                    type="radio"
                    name="qualifications"
                    value={option}
                    className="text-purple-600"
                    onChange={(e) => setFormData({...formData, hasQualifications: e.target.value})}
                  />
                  <span className="ml-2">{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Do you have a Police Check, or are you willing to obtain one?
            </label>
            <div className="space-x-4">
              {['Yes', 'No'].map(option => (
                <label key={option} className="inline-flex items-center">
                  <input
                    type="radio"
                    name="policeCheck"
                    value={option}
                    className="text-purple-600"
                    onChange={(e) => setFormData({...formData, hasPoliceCheck: e.target.value})}
                  />
                  <span className="ml-2">{option}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">If successful, how soon can you start?</label>
            <input
              type="text"
              required
              className="w-full p-2 border border-purple-300 rounded"
              onChange={(e) => setFormData({...formData, startDate: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Tell us something about yourself and why you would be a great addition to our team?
            </label>
            <textarea
              required
              rows="4"
              className="w-full p-2 border border-purple-300 rounded"
              onChange={(e) => setFormData({...formData, aboutYourself: e.target.value})}
            />
          </div>

          {/* Replace the resume/cover letter link inputs with file uploads */}
          {fileInputs}

          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Careers Component
const Careers = () => {
  const [careers, setCareers] = useState([]);
  const [selectedState, setSelectedState] = useState('all');
  const [selectedJobType, setSelectedJobType] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const quillInstances = useRef({});

  useEffect(() => {
    const fetchCareers = async () => {
      try {
        const response = await fetch(`${BASE_URL}/api/careers`);
        const data = await response.json();
        console.log(data);
        setCareers(data);
      } catch (error) {
        console.error('Error fetching careers:', error);
      }
    };

    fetchCareers();
  }, []);

  const handleJobTypeClick = (jobType) => {
    setSelectedJobType(jobType);
  };

  const handleApplyClick = (job) => {
    setSelectedJob(job);
    setIsModalOpen(true);
  };

  return (
    <>
    <div className="min-h-screen bg-white">
      {/* Banner Section */}
      <TitleBanner image="/Website_Title_Images/Careers.svg" title="Work with us." subheading="Find a valuable career at Livability Care Australia" />
      

      {/* Company Culture Section */}
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-6">
        <p className="text-lg text-gray-700">
          At Livability Care Australia, we offer a supportive, open-culture team environment where your thoughts and ideas are heard and welcomed. We're growing quickly, but we're growing together, and we recognise the difference just one individual can make.
        </p>
        <p className="text-lg text-gray-700">
          By joining our team, you're not just supporting people—you're making a meaningful difference in the lives of our participants. Here, your talents will be acknowledged, and you'll have opportunities to grow both personally and professionally.
        </p>
      </div>

      {/* Equal Opportunity Section */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-primary mb-4">Equal Opportunity Employer</h2>
          <p className="text-gray-700">
            Livability Care Australia is an equal opportunity employer. We provide the same benefits and opportunities for hiring and advancement to everyone without discrimination.
          </p>
        </div>
      </div>

      {/* Employee Benefits Section */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-primary mb-4">Employee Benefits</h2>
        <p className="text-gray-700 mb-8">
          We prioritise the well-being of our employees by offering a range of benefits that support a healthy work-life balance.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 place-items-center">
          <div className="flex items-center justify-center">
            <img 
              src="https://walltowallride.com/wp-content/uploads/2022/03/Maxxia-no-background-2022.png" 
              alt="Maxima" 
              className="w-28 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/de/BankSA_logo.svg/960px-BankSA_logo.svg.png?20160908171520" 
              alt="BankSA" 
              className="w-28 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="/EmpBenefits/frankie4.jpg" 
              alt="FRANKiE4" 
              className="w-28 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="https://eapassist.com.au/wp-content/uploads/2019/12/EapAssistLogo.png" 
              alt="EapAssist" 
              className="w-28 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="https://www.questapartments.com.au/App_Styles/images/Quest_PrimaryLogo_Grey_RGB.svg" 
              alt="Quest" 
              className="w-28 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="https://images.squarespace-cdn.com/content/v1/5b96fad570e8022dfb162f35/8d58b761-4fa4-4235-ba88-d7f264804e16/Goodlife.png?format=500w" 
              alt="Goodlife Health Clubs" 
              className="w-28 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="/EmpBenefits/af.jpg" 
              alt="Anytime Fitness" 
              className="w-28 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="/EmpBenefits/aviator_bar.png" 
              alt="Aviator Bar" 
              className="w-28 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="/EmpBenefits/ev.png" 
              alt="Ember & Wine" 
              className="w-28 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="/EmpBenefits/medibank.png" 
              alt="Medibank" 
              className="w-28 object-contain"
            />
          </div>
          <div className="flex items-center justify-center">
            <img 
              src="/EmpBenefits/westpac.png" 
              alt="Westpac" 
              className="w-28 object-contain"
            />
          </div>
        </div>
      </div>

      {/* Current Vacancies Section */}
      <div className="bg-primary py-12">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white mb-8">Current Job Vacancies</h2>
          
          {/* Filters */}
          <div className="flex gap-4 mb-8">
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="p-2 rounded border-2 border-purple-300"
            >
              <option value="all">All States</option>
              <option value="South Australia">South Australia</option>
              <option value="Victoria">Victoria</option>
              <option value="Queensland">Queensland</option>
            </select>

            <select
              value={selectedJobType}
              onChange={(e) => setSelectedJobType(e.target.value)}
              className="p-2 rounded border-2 border-purple-300"
            >
              <option value="all">All Types</option>
              <option value="Disability Support Worker">Disability Support Worker</option>
              <option value="Other Vacancies">Other Vacancies</option>
            </select>
          </div>

          {/* Job Categories */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {['Disability Support Worker', 'Other Vacancies', 'Recruitment Enquiries'].map((category) => (
              <div 
                key={category}
                className={`bg-white p-6 rounded-lg shadow-lg text-center cursor-pointer ${
                  selectedJobType === category ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => category !== 'Recruitment Enquiries' && handleJobTypeClick(category)}
              >
                <div className="w-32 h-32 mx-auto mb-4 bg-white flex items-center justify-center">
                  <img 
                    src={category === 'Disability Support Worker' 
                      ? '/Icons/LGA-Careers-DisabilitySupportWorkers-Icon.svg' 
                      : category === 'Other Vacancies' 
                      ? '/Icons/LGA-Careers-OtherVacancies-Icon.svg' 
                      : '/Icons/LGA-Careers-RecruitmentEnquiries-Icon.svg'
                    }
                    alt={category}
                    className="w-28 h-28"
                  />
                </div>
                <h3 className="text-xl font-bold text-[#336fb8]">{category}</h3>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Job Listings */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className={`${careers.filter(job => (selectedState === 'all' || job.location === selectedState) &&
                            (selectedJobType === 'all' || job.jobType === selectedJobType)).length > 0 ? 'space-y-6 rounded-xl border-primary border-2 p-8' : ''}`}>
          <div className=''>
            {careers
              .filter(job => (selectedState === 'all' || job.location === selectedState) &&
                            (selectedJobType === 'all' || job.jobType === selectedJobType))
              .map((job) => (
                <div key={job.id} className="bg-white p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-primary">{job.jobTitle}</h3>
                      <p className="text-gray-600">LOCATION: {job.location}</p>
                      <p className="text-gray-600">CODE: {job.jobCode}</p>
                      <p className="text-gray-600">TYPE: {job.jobType}</p>
                    </div>
                    <button
                      onClick={() => handleApplyClick(job)}
                      className="bg-green-500 text-white px-6 py-2 rounded-full shadow-md hover:bg-green-600 transition-colors"
                    >
                      Apply
                    </button>
                  </div>
                  <div className="mt-4">
                    <h4 className="font-bold text-gray-700">JOB DESCRIPTION:</h4>
                    <div id={`description-${job.id}`} className="mt-2 prose max-w-none">
                      <div 
                        className="ql-editor"
                        dangerouslySetInnerHTML={{ __html: job.description }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              </div>
          </div>
        </div>

      {/* Application Modal */}
      {selectedJob && (
        <ApplicationModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedJob(null);
          }}
          jobTitle={selectedJob.jobTitle}
          jobLocation={selectedJob.location}
          jobCode={selectedJob.jobCode}
        />
      )}

      {/* No Results Message */}
      {careers.filter(job => 
        (selectedState === 'all' || job.location === selectedState) &&
        (selectedJobType === 'all' || job.jobType === selectedJobType)
      ).length === 0 && (
        <div className="text-center py-12">
          <h3 className="text-xl text-gray-600">No positions available matching your criteria.</h3>
          <p className="text-gray-500 mt-2">Please try adjusting your filters or check back later.</p>
        </div>
      )}
    </div>
    <TestimonialsSlider />
    </>
  );
};

export default Careers;